import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  buildAuditCsvUrl,
  fetchAuditEntries,
  type AuditQuery,
} from '../../lib/api/projects';
import type {
  AuditChangeType,
  AuditEntry,
} from '../../../../shared/types/projects';
import { authFetch } from '../../lib/api/authFetch';

/**
 * FR-7 — audit log viewer.
 *
 * Chronological table newest-first, filter chips for changeType + actor +
 * target + date range, paginate via cursor. CSV export hits the .csv endpoint
 * with the same filter params; the auth token rides in a fetch header (we
 * can't put it on the <a> directly), so the button stitches the file blob
 * and triggers a synthetic download.
 */

const CHANGE_TYPES: AuditChangeType[] = [
  'create-project',
  'update-settings',
  'attach-repo',
  'detach-repo',
  'update-overrides',
  'update-guardrails',
  'update-repo-guardrails',
  'archive',
  'refresh-config',
];

export const AuditTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [filters, setFilters] = useState<AuditQuery>({ limit: 50 });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['project', projectId, 'audit', filters],
    queryFn: () => fetchAuditEntries(projectId, filters),
  });

  const setFilter = <K extends keyof AuditQuery>(k: K, v: AuditQuery[K]) =>
    setFilters((prev) => ({ ...prev, [k]: v }));

  const handleCsvDownload = async () => {
    const url = buildAuditCsvUrl(projectId, filters);
    try {
      const r = await authFetch(url);
      if (!r.ok) throw new Error(`Download failed: ${r.statusText}`);
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `audit-${projectId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-3">
      <FilterBar
        filters={filters}
        setFilter={setFilter}
        onClear={() => setFilters({ limit: 50 })}
        onCsv={handleCsvDownload}
      />

      {query.isLoading && <div className="text-sm text-gray-500">Loading audit log…</div>}
      {query.isError && (
        <div role="alert" className="text-sm text-red-600">
          {(query.error as Error).message}
        </div>
      )}
      {query.isSuccess && query.data.entries.length === 0 && (
        <div
          data-testid="audit-empty"
          className="border rounded p-6 text-center text-sm text-gray-500 bg-gray-50"
        >
          No audit entries match these filters.
        </div>
      )}

      {query.isSuccess && query.data.entries.length > 0 && (
        <div className="border rounded overflow-x-auto" data-testid="audit-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-2 py-1.5 font-medium">When</th>
                <th className="px-2 py-1.5 font-medium">Actor</th>
                <th className="px-2 py-1.5 font-medium">Change</th>
                <th className="px-2 py-1.5 font-medium">Target</th>
                <th className="px-2 py-1.5 font-medium">Version</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {query.data.entries.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={entry}
                  expanded={expandedRow === entry.id}
                  onToggle={() =>
                    setExpandedRow((cur) => (cur === entry.id ? null : entry.id))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {query.isSuccess && query.data.nextCursor && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setFilter('cursor', query.data.nextCursor ?? undefined)}
            className="text-xs border rounded px-3 py-1 hover:bg-gray-50"
          >
            Load older entries
          </button>
        </div>
      )}
    </div>
  );
};

const FilterBar: React.FC<{
  filters: AuditQuery;
  setFilter: <K extends keyof AuditQuery>(k: K, v: AuditQuery[K]) => void;
  onClear: () => void;
  onCsv: () => void;
}> = ({ filters, setFilter, onClear, onCsv }) => (
  <div className="border rounded p-2 flex flex-wrap items-end gap-2 text-xs">
    <FilterField label="actor">
      <input
        type="text"
        value={filters.actor ?? ''}
        onChange={(e) => setFilter('actor', e.target.value || undefined)}
        placeholder="alice@example.com"
        className="border rounded px-1 py-0.5 font-mono"
      />
    </FilterField>
    <FilterField label="change type">
      <select
        value={filters.changeType ?? ''}
        onChange={(e) => setFilter('changeType', (e.target.value || undefined) as AuditChangeType | undefined)}
        className="border rounded px-1 py-0.5 bg-white"
      >
        <option value="">— any —</option>
        {CHANGE_TYPES.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
    </FilterField>
    <FilterField label="target">
      <input
        type="text"
        value={filters.target ?? ''}
        onChange={(e) => setFilter('target', e.target.value || undefined)}
        placeholder="REPO#org/repo"
        className="border rounded px-1 py-0.5 font-mono"
      />
    </FilterField>
    <FilterField label="from">
      <input
        type="datetime-local"
        value={localFromIso(filters.from)}
        onChange={(e) =>
          setFilter('from', e.target.value ? new Date(e.target.value).toISOString() : undefined)
        }
        className="border rounded px-1 py-0.5"
      />
    </FilterField>
    <FilterField label="to">
      <input
        type="datetime-local"
        value={localFromIso(filters.to)}
        onChange={(e) =>
          setFilter('to', e.target.value ? new Date(e.target.value).toISOString() : undefined)
        }
        className="border rounded px-1 py-0.5"
      />
    </FilterField>
    <div className="ml-auto flex gap-1">
      <button
        type="button"
        onClick={onClear}
        className="border rounded px-2 py-0.5 hover:bg-gray-50"
      >
        Clear
      </button>
      <button
        type="button"
        onClick={onCsv}
        data-testid="export-csv"
        className="border rounded px-2 py-0.5 bg-white hover:bg-gray-50"
      >
        Export CSV
      </button>
    </div>
  </div>
);

const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

const AuditRow: React.FC<{
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}> = ({ entry, expanded, onToggle }) => (
  <>
    <tr className="border-t hover:bg-gray-50">
      <td className="px-2 py-1 font-mono">
        {new Date(entry.timestamp).toLocaleString()}
      </td>
      <td className="px-2 py-1 font-mono">{entry.actor}</td>
      <td className="px-2 py-1">
        <span className="inline-block bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">
          {entry.changeType}
        </span>
      </td>
      <td className="px-2 py-1 font-mono truncate max-w-[200px]" title={entry.target}>
        {entry.target}
      </td>
      <td className="px-2 py-1 font-mono">
        {entry.previousVersion !== null && entry.newVersion !== null
          ? `v${entry.previousVersion ?? 0} → v${entry.newVersion}`
          : '—'}
      </td>
      <td className="px-2 py-1 text-right">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="text-blue-600 hover:underline text-xs"
        >
          {expanded ? 'Hide' : 'Diff'}
        </button>
      </td>
    </tr>
    {expanded && (
      <tr className="bg-gray-50 border-t">
        <td colSpan={6} className="px-2 py-2">
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <header className="text-gray-500 mb-0.5">before</header>
              <pre className="bg-white border rounded p-1 max-h-48 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(entry.before, null, 2)}
              </pre>
            </div>
            <div>
              <header className="text-gray-500 mb-0.5">after</header>
              <pre className="bg-white border rounded p-1 max-h-48 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(entry.after, null, 2)}
              </pre>
            </div>
          </div>
          {entry.notes && (
            <div className="text-[10px] mt-2 text-gray-700">note: {entry.notes}</div>
          )}
        </td>
      </tr>
    )}
  </>
);

function localFromIso(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

export default AuditTab;
